import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import axios from "axios";

interface User {
  username: string;
  email: string;
  telegram_chat_id?: string;
  is_admin?: boolean;
  is_disabled?: boolean;
  must_change_password?: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  register: (
    username: string,
    email: string,
    password: string,
  ) => Promise<void>;
  logout: () => void;
  updateProfile: (telegramChatId: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Set up axios interceptor to add auth header and handle token refresh
  useEffect(() => {
    // Ensure we do not use cookie-based auth; use JWT tokens passed in request bodies/headers
    axios.defaults.withCredentials = false;

    const token = localStorage.getItem("access_token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }

    // Track refresh state to prevent multiple simultaneous refresh attempts
    let isRefreshing = false;
    let failedQueue: Array<{
      resolve: (value?: any) => void;
      reject: (reason?: any) => void;
    }> = [];
    let refreshAttempts = 0;
    const MAX_REFRESH_ATTEMPTS = 1;

    const processQueue = (error: any, token: string | null = null) => {
      failedQueue.forEach((prom) => {
        if (error) {
          prom.reject(error);
        } else {
          prom.resolve(token);
        }
      });

      failedQueue = [];
    };

    // Response interceptor to handle 401 errors and refresh token
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Skip refresh for the refresh endpoint itself to prevent infinite loops
        if (originalRequest.url?.includes('/api/auth/refresh')) {
          return Promise.reject(error);
        }

        // If 401 and we haven't exceeded max attempts
        if (error.response?.status === 401 && !originalRequest._retry) {
          // If already refreshing, queue this request
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
                return axios(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          refreshAttempts++;

          // Prevent infinite refresh loops
          if (refreshAttempts > MAX_REFRESH_ATTEMPTS) {
            console.error("Max refresh attempts exceeded, logging out");
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            delete axios.defaults.headers.common["Authorization"];
            setIsAuthenticated(false);
            setUser(null);
            window.location.href = "/login";
            return Promise.reject(error);
          }

          isRefreshing = true;

          try {
            const refreshToken = localStorage.getItem("refresh_token");
            if (!refreshToken) {
              throw new Error("No refresh token available");
            }

            const response = await axios.post("/api/auth/refresh", {
              refresh_token: refreshToken,
            });

            const { access_token, refresh_token: new_refresh_token } =
              response.data;

            localStorage.setItem("access_token", access_token);
            localStorage.setItem("refresh_token", new_refresh_token);
            axios.defaults.headers.common["Authorization"] =
              `Bearer ${access_token}`;

            // Reset attempts on success
            refreshAttempts = 0;
            processQueue(null, access_token);

            // Retry the original request
            originalRequest.headers["Authorization"] = `Bearer ${access_token}`;
            return axios(originalRequest);
          } catch (refreshError) {
            // Refresh failed, logout user
            processQueue(refreshError, null);
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            delete axios.defaults.headers.common["Authorization"];
            setIsAuthenticated(false);
            setUser(null);
            
            // Only redirect to login once
            if (!window.location.pathname.includes('/login')) {
              window.location.href = "/login";
            }
            
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }

        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      // If the backend signals that initial setup is required, stop auth checks
      // and send the user to the setup screen without hammering refresh/profile.
      const needsSetup = await checkInitialSetup();
      if (needsSetup) {
        return;
      }
      
      // Add a small delay to prevent all tabs from hitting the API simultaneously
      const randomDelay = Math.random() * 500; // 0-500ms random delay
      await new Promise(resolve => setTimeout(resolve, randomDelay));
      
      await checkAuth();
    };

    bootstrap();
  }, []);

  const checkInitialSetup = async (): Promise<boolean> => {
    try {
      // Don't redirect if we're already on the initial-setup page
      if (window.location.pathname === "/initial-setup") {
        setLoading(false);
        return true;
      }

      const resp = await axios.get("/api/auth/initial-setup");
      if (resp.data?.needs_setup) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        delete axios.defaults.headers.common["Authorization"];
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
        window.location.href = "/initial-setup";
        return true;
      }
    } catch (err) {
      // ignore and fall back to normal auth flow
    }
    return false;
  };

  const checkAuth = async () => {
    try {
      // Check if we have a token stored
      const token = localStorage.getItem("access_token");
      if (!token) {
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
        return;
      }

      const response = await axios.get("/api/profile");
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error: any) {
      // Only clear tokens if it's an authentication error
      // Avoid clearing on network errors or other issues
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        setUser(null);
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        delete axios.defaults.headers.common["Authorization"];
      } else {
        // For other errors (network, server errors), just mark as not authenticated
        // but don't clear tokens - they might still be valid
        console.error("Auth check failed with non-401 error:", error.message);
        setIsAuthenticated(false);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string) => {
    // Call login endpoint to get JWT tokens
    const response = await axios.post("/api/auth/login", {
      username,
      password,
    });

    const { access_token, refresh_token } = response.data;

    // Store tokens
    localStorage.setItem("access_token", access_token);
    localStorage.setItem("refresh_token", refresh_token);

    // Set default Authorization header
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;

    // Fetch user profile
    await checkAuth();
  };

  const register = async (
    username: string,
    email: string,
    password: string,
  ) => {
    // Public registration is disabled; accounts must be created by an administrator.
    return Promise.reject(
      new Error(
        "Registration is disabled. Only administrators can create accounts.",
      ),
    );
  };

  const logout = async () => {
    try {
      // Call logout endpoint (no cookie clearing in JWT flow)
      await axios.post("/api/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    }

    // Clear stored tokens
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    delete axios.defaults.headers.common["Authorization"];

    setIsAuthenticated(false);
    setUser(null);
  };

  const updateProfile = async (telegramChatId: string) => {
    await axios.put("/api/profile", { telegram_chat_id: telegramChatId });
    await checkAuth();
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        register,
        logout,
        updateProfile,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
